import { TellescopeModelFromName } from "../../mappings";
import { PLACEHOLDER_FOR_UNKNOWN_PHONE } from "./unknown-phone-placeholder";

const DEFAULT_INBOX_STATUS = "New";

export const EXCLUDED_INBOX_THREAD_KEYS_FROM_SMS = [
  "id",
  "createdAt",
  "updatedAt",
  "businessId",
  "creator",
  "organizationIds",
  "sharedWithOrganizations",
] as const satisfies (keyof TellescopeModelFromName<"inbox_threads">)[];

type ExcludedInboxThreadKeys = (typeof EXCLUDED_INBOX_THREAD_KEYS_FROM_SMS)[number];
type PartialInboxThread = Omit<TellescopeModelFromName<"inbox_threads">, ExcludedInboxThreadKeys>;

export const removeSMSExcludedInboxThreadFields = <T extends Partial<TellescopeModelFromName<"inbox_threads">>>(
  inboxThread: T
): PartialInboxThread => {
  const entries = Object.entries(inboxThread);
  return Object.fromEntries(
    entries.filter(([key]) => !EXCLUDED_INBOX_THREAD_KEYS_FROM_SMS.includes(key as ExcludedInboxThreadKeys))
  ) as PartialInboxThread;
};

/**
 * Converts an SMS message to an InboxThread representation.
 * This follows the same logic as buildSMSThreads in api/modules/inbox.ts
 *
 * Purpose: Used to determine if a new SMS message matches cached inbox_threads
 * getSome filters to decide if the cache should be updated/invalidated.
 *
 * @param sms - The SMS message to convert
 * @returns An InboxThread representation of the SMS message
 */
export const convertSmsMessageToInboxThread = (sms: TellescopeModelFromName<"sms_messages">): PartialInboxThread => {
  const now = new Date();

  // Determine if this is a draft or scheduled message
  const isDraft = sms.isDraft;
  const isScheduled = !isDraft && sms.sendAt && new Date(sms.sendAt) > now;

  // For drafts and scheduled messages, we still create thread representation
  // but they don't affect preview/timestamp in the same way
  const useForPreview = !isDraft && !isScheduled;

  return {
    type: "SMS",
    title: sms.inbound ? sms.enduserPhoneNumber || "" : sms.enduserPhoneNumber || "",
    preview: useForPreview ? sms.message : "",
    timestamp: new Date(sms.createdAt || new Date()),
    assignedTo: sms.assignedTo || [],
    userIds: !sms.inbound && sms.userId ? [sms.userId] : [],
    enduserIds: sms.enduserId ? [sms.enduserId] : [],
    inboxStatus: sms.inbound ? sms.inboxStatus || DEFAULT_INBOX_STATUS : DEFAULT_INBOX_STATUS,
    threadId: sms.id,
    tags: sms.inbound ? sms.tags || [] : [],
    phoneNumber: sms.phoneNumber || PLACEHOLDER_FOR_UNKNOWN_PHONE,
    enduserPhoneNumber: sms.enduserPhoneNumber || PLACEHOLDER_FOR_UNKNOWN_PHONE,
    readBy: sms.inbound ? sms.readBy || {} : {},
    outboundPreview: !sms.inbound ? sms.message : "",
    outboundTimestamp: !sms.inbound ? new Date(sms.createdAt || new Date()) : undefined,
    ...(sms.userId && !sms.inbound ? { recentOutboundUserId: sms.userId } : {}),
    ...(sms.enduserId && sms.inbound ? { recentInboundEnduserId: sms.enduserId } : {}),
    archivedAt: sms.archivedAt || "",
    trashedAt: sms.trashedAt || "",
    draftMessageIds: isDraft ? [sms.id] : [],
    scheduledMessageIds: isScheduled ? [sms.id] : [],
  };
};
